import React, {
  createContext, ReactElement, useCallback, useEffect, useState,
} from 'react';
import ReactDOM from 'react-dom';
import { InstantSearch } from 'react-instantsearch-dom';
import { BrowserRouter, Route } from 'react-router-dom';
import {
  ALGOLIA_ALL_LISTINGS_INDEX,
  ALGOLIA_SORT_BY_LOCATION_IF_SET,
  listingSearchRefinements,
} from '../../../utils/constants';
import useURLSync from '../../../hooks/useURLSync';
import fetchSearchParams from '../../../utils/fetchSearchParams';
import { SortParams } from '../../search/admin-dashboard-tables/interfaces';
import { useAlgoliaSearchClientLite } from '../../../utils/hooks/useAlgoliaSearchClient';
import useLocationSortOnLoad from '../../../utils/useLocationSortOnLoad';
import PriceCarousel from '../slider/price-carousel';
import SearchResultsBlock from './search-results-block';

export const AlgoliaContext = createContext('');

const Search = (props) => {
  const {
    children,
    newArrivals,
    instantRefinements = [],
  } = props;

  const getInitialSort = (isNewArrivals: boolean) => {
    const {
      lat,
      lng,
      sortBy,
    }: SortParams = fetchSearchParams();
    if (
      (lat && lng)
      || (
        // If location is set and no sort is specified, we can default to location
        ALGOLIA_SORT_BY_LOCATION_IF_SET
        && localStorage.getItem('latitude')
        && localStorage.getItem('longitude')
        && !sortBy
      )
    ) {
      return `${ALGOLIA_ALL_LISTINGS_INDEX}_location`;
    }
    if (sortBy) {
      return sortBy;
    }
    if (isNewArrivals) {
      return `${ALGOLIA_ALL_LISTINGS_INDEX}_created_at_desc`;
    }
    return ALGOLIA_ALL_LISTINGS_INDEX!;
  };

  const getInitialIndexName = (currentSort: string) => {
    const { sortBy }: SortParams = fetchSearchParams();

    return sortBy
      || currentSort
      || ALGOLIA_ALL_LISTINGS_INDEX!;
  };

  // This is the value used to track the form state (not the indexName passed to Algolia)
  const [currentSort, setCurrentSort] = useState(getInitialSort(newArrivals));
  // This is the actual indexName passed to Algolia
  const [indexName, setIndexName] = useState(getInitialIndexName(currentSort));

  const shouldSortByLocationOnLoad = currentSort === `${ALGOLIA_ALL_LISTINGS_INDEX}_location`;

  const { syncCoordinatesToUrl } = useLocationSortOnLoad();

  if (shouldSortByLocationOnLoad) {
    syncCoordinatesToUrl(props.history);
  }

  const {
    user,
    searchState,
    setSearchState,
    createURL,
    resetPagination,
  } = useURLSync(listingSearchRefinements);

  const { searchClient } = useAlgoliaSearchClientLite(user?.public_key);

  useEffect(() => {
    if (currentSort) {
      setIndexName(currentSort);
      // Reset pagination whenever sort is changed
      resetPagination(currentSort);
    }
  }, [currentSort]);

  /**
   *  This method prevents the connected components and the internal search state setter
   *  from updating the refinements that are “applicable”/non-instant.
   */
  const customSearchStateChange = useCallback((newSearchState) => {
    setSearchState((currentSearchState) => {
      const updatedConnectedRefinementList = {};
      const { refinementList: newRefinementList = {} } = newSearchState;
      Object.keys(newRefinementList)
        .forEach((refinementKey) => {
          if (instantRefinements.includes(refinementKey)) {
            updatedConnectedRefinementList[refinementKey] = newRefinementList[refinementKey];
          }
        });
      return ({
        ...newSearchState,
        refinementList: {
          ...currentSearchState.refinementList,
          ...updatedConnectedRefinementList,
        },
      });
    });
  }, [setSearchState]);

  return (
    <AlgoliaContext.Provider value={indexName}>
      <InstantSearch
        searchClient={searchClient}
        indexName={indexName}
        searchState={searchState}
        createURL={createURL}
        onSearchStateChange={customSearchStateChange}
      >
        {React.Children.map(children, (child) => (
          child && React.cloneElement(child, {
            searchState, setSearchState, currentSort, setCurrentSort, shouldSortByLocationOnLoad,
          })
        ))}
      </InstantSearch>
    </AlgoliaContext.Provider>
  );
};

const SearchComponent = Search;

const SearchContainer: React.FC<any> = (props: any): ReactElement => {
  const {
    breedSlug,
    sellerSlug,
    listingsType,
    categorySlug,
    showNonPublished = false,
    filterByState,
    filterByCity,
    cityCoordinates,
    onlyOnSale,
    availableToday,
    puppiesUnderPrice,
    newArrivals,
    priceUrls,
  } = props;
  return (
    <SearchComponent {...props}>
      <SearchResultsBlock
        {...props}
        breedSlug={breedSlug}
        sellerSlug={sellerSlug}
        listingsType={listingsType}
        categorySlug={categorySlug}
        showNonPublished={showNonPublished}
        filterByState={filterByState}
        filterByCity={filterByCity}
        cityCoordinates={cityCoordinates}
        onlyOnSale={onlyOnSale}
        availableToday={availableToday}
        puppiesUnderPrice={puppiesUnderPrice}
        newArrivals={newArrivals}
      />
      <PriceCarousel priceUrls={priceUrls} />
    </SearchComponent>
  );
};

export { SearchContainer, SearchComponent };

if (document.getElementsByClassName('react-search').length > 0) {
  document.querySelectorAll('.react-search')
    .forEach((element) => {
      const { user } = window;
      const seller = element.getAttribute('data-seller') ?? '';
      const listingsType = element.getAttribute('data-listingstype') || 'puppy';
      const showNonPublished = element.getAttribute('data-shownonpublished') ?? false;
      const category = element.getAttribute('data-category') ?? null;
      const filterByState = element.getAttribute('data-state') ?? '';
      const filterByCity = element.getAttribute('data-city') ?? '';
      const cityCoordinates = JSON.parse(element.getAttribute('data-city-coordinates') || 'null');
      const onlyOnSale = element.getAttribute('data-puppies-on-sale') ?? '';
      const availableToday = element.getAttribute('data-puppies-available-today') ?? '';
      const puppiesUnderPrice = element.getAttribute('data-puppies-under-price') ?? '';
      const newArrivals = element.getAttribute('data-new-arrivals') ?? '';
      const priceUrls = JSON.parse(element.getAttribute('data-price-urls') || '[]');
      ReactDOM.render(
        <BrowserRouter>
          <Route
            path="/"
            render={(routeProps) => {
              return (
                <SearchContainer
                  sellerSlug={seller}
                  categorySlug={category}
                  user={user}
                  listingsType={listingsType}
                  showNonPublished={!!showNonPublished}
                  filterByState={filterByState}
                  filterByCity={filterByCity}
                  cityCoordinates={cityCoordinates}
                  onlyOnSale={onlyOnSale}
                  availableToday={availableToday}
                  puppiesUnderPrice={puppiesUnderPrice}
                  newArrivals={newArrivals}
                  instantRefinements={[
                    'admin_breed_parents',
                    'admin_single_breed',
                  ]}
                  priceUrls={priceUrls}
                  {...routeProps}
                />
              );
            }}
          />
        </BrowserRouter>,
        element,
      );
    });
}
